import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  CreateStoreArticleDto,
  CreateStoreDepartmentDto,
  ReorderStoreDepartmentsDto,
  StoreArticle,
  StoreDepartment,
  UpdateStoreArticleDto,
  UpdateStoreDepartmentDto,
} from '@mrrmlab/shared-types';
import { useApiClient } from './api-client-context';

export const catalogKeys = {
  departments: (storeId: string) => ['catalog', 'departments', storeId] as const,
  articles: (storeId: string) => ['catalog', 'articles', storeId] as const,
};

type MutationExtras<TData, TVars> = Omit<
  UseMutationOptions<TData, Error, TVars>,
  'mutationFn'
>;

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

export function useStoreDepartments(
  storeId: string,
  options?: Omit<UseQueryOptions<StoreDepartment[]>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<StoreDepartment[]>({
    queryKey: catalogKeys.departments(storeId),
    queryFn: ({ signal }) => client.storeDepartments.list(storeId, signal),
    ...options,
  });
}

export function useCreateStoreDepartment(
  storeId: string,
  options?: MutationExtras<StoreDepartment, CreateStoreDepartmentDto>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<StoreDepartment, Error, CreateStoreDepartmentDto>({
    mutationFn: (dto) => client.storeDepartments.create(storeId, dto),
    ...options,
    onSuccess: (data, vars, ctx, meta) => {
      qc.invalidateQueries({ queryKey: catalogKeys.departments(storeId) });
      options?.onSuccess?.(data, vars, ctx, meta);
    },
  });
}

export function useUpdateStoreDepartment(
  storeId: string,
  options?: MutationExtras<StoreDepartment, { id: string; dto: UpdateStoreDepartmentDto }>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<StoreDepartment, Error, { id: string; dto: UpdateStoreDepartmentDto }>({
    mutationFn: ({ id, dto }) => client.storeDepartments.update(storeId, id, dto),
    ...options,
    onSuccess: (data, vars, ctx, meta) => {
      qc.invalidateQueries({ queryKey: catalogKeys.departments(storeId) });
      options?.onSuccess?.(data, vars, ctx, meta);
    },
  });
}

export function useDeleteStoreDepartment(
  storeId: string,
  options?: MutationExtras<void, string>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.storeDepartments.delete(storeId, id),
    ...options,
    onSuccess: (data, id, ctx, meta) => {
      qc.invalidateQueries({ queryKey: catalogKeys.departments(storeId) });
      qc.invalidateQueries({ queryKey: catalogKeys.articles(storeId) });
      options?.onSuccess?.(data, id, ctx, meta);
    },
  });
}

export function useReorderStoreDepartments(
  storeId: string,
  options?: MutationExtras<void, ReorderStoreDepartmentsDto>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, ReorderStoreDepartmentsDto>({
    mutationFn: (dto) => client.storeDepartments.reorder(storeId, dto),
    ...options,
    onMutate: async (dto) => {
      await qc.cancelQueries({ queryKey: catalogKeys.departments(storeId) });
      const previous = qc.getQueryData<StoreDepartment[]>(catalogKeys.departments(storeId));
      if (previous) {
        const sorted = dto.orderedIds
          .map((id) => previous.find((d) => d.id === id))
          .filter((d): d is StoreDepartment => d !== undefined);
        const rest = previous.filter((d) => !dto.orderedIds.includes(d.id));
        qc.setQueryData(catalogKeys.departments(storeId), [...sorted, ...rest]);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      const c = ctx as { previous?: StoreDepartment[] } | undefined;
      if (c?.previous) {
        qc.setQueryData(catalogKeys.departments(storeId), c.previous);
      }
    },
    onSuccess: (data, vars, ctx, meta) => {
      qc.invalidateQueries({ queryKey: catalogKeys.departments(storeId) });
      options?.onSuccess?.(data, vars, ctx, meta);
    },
  });
}

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

export function useStoreArticles(
  storeId: string,
  options?: Omit<UseQueryOptions<StoreArticle[]>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<StoreArticle[]>({
    queryKey: catalogKeys.articles(storeId),
    queryFn: ({ signal }) => client.storeArticles.list(storeId, signal),
    ...options,
  });
}

export function useCreateStoreArticle(
  storeId: string,
  options?: MutationExtras<StoreArticle, CreateStoreArticleDto>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<StoreArticle, Error, CreateStoreArticleDto>({
    mutationFn: (dto) => client.storeArticles.create(storeId, dto),
    ...options,
    onSuccess: (data, vars, ctx, meta) => {
      qc.invalidateQueries({ queryKey: catalogKeys.articles(storeId) });
      options?.onSuccess?.(data, vars, ctx, meta);
    },
  });
}

export function useUpdateStoreArticle(
  storeId: string,
  options?: MutationExtras<StoreArticle, { id: string; dto: UpdateStoreArticleDto }>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<StoreArticle, Error, { id: string; dto: UpdateStoreArticleDto }>({
    mutationFn: ({ id, dto }) => client.storeArticles.update(storeId, id, dto),
    ...options,
    onSuccess: (data, vars, ctx, meta) => {
      qc.invalidateQueries({ queryKey: catalogKeys.articles(storeId) });
      options?.onSuccess?.(data, vars, ctx, meta);
    },
  });
}

export function useDeleteStoreArticle(
  storeId: string,
  options?: MutationExtras<void, string>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.storeArticles.delete(storeId, id),
    ...options,
    onSuccess: (data, id, ctx, meta) => {
      qc.invalidateQueries({ queryKey: catalogKeys.articles(storeId) });
      options?.onSuccess?.(data, id, ctx, meta);
    },
  });
}
